import type { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { routerWithQueryClient } from '@tanstack/react-router-with-query';

import { getQueryClient } from './_shared/data/query-client';
import { routeTree } from './routeTree.gen';

function DefaultNotFound() {
	return (
		<main>
			<h1>Not found</h1>
			<p>The page you were looking for does not exist.</p>
		</main>
	);
}

// `context.queryClient` is threaded through every route's loader so
// route-level loaders can pre-warm the Tanstack Query cache via
// `context.queryClient.ensureQueryData(...)`. Components then call
// `useQuery` with the same key and get the cached data synchronously
// on first render — no loading flash.
//
// `routerWithQueryClient` wraps the router so the SSR-side cache is
// dehydrated into the server response and re-hydrated on the client.
// Without this wrapper the server's pre-warmed cache is discarded on
// hydration and `useQuery` triggers a refetch — surfacing as a brief
// empty / loading flash before the data appears. Tanstack Start's
// canonical pattern.
export function getRouter() {
	const queryClient: QueryClient = getQueryClient();
	const router = createRouter({
		routeTree,
		context: { queryClient },
		scrollRestoration: true,
		defaultNotFoundComponent: DefaultNotFound,
		// Preload a route's beforeLoad + loader on link hover / touch-start so the
		// shell + flag queries are usually warm by the time the user commits to the
		// navigation.
		defaultPreload: 'intent',
		// Cap how often intent-preload re-runs a route's loader: treat a preloaded
		// loader result as fresh for 30s so repeated hovers over the same link don't
		// re-run it. The underlying fetches are deduped independently by the
		// per-query staleTimes (auth-shell + flags at 60s), so this only governs the
		// router-level loader cache.
		defaultPreloadStaleTime: 30_000,
	});
	return routerWithQueryClient(router, queryClient);
}

declare module '@tanstack/react-router' {
	interface Register {
		router: ReturnType<typeof getRouter>;
	}
}
